/**
 * aggregation.service.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { Algorithm } from "../algorithms/structure/";
import { Group } from "../auth/identity/group";
import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { SettingService } from "../configuration/setting.service";
import { AGGREGATION_MODULE_URL } from "../routing/app.routing";
import { SocketFile } from "../server/connection/download-socket";
import { MessageReceiver } from  "../server/message-receiver";
import { Message, MessageTag } from "../server/messages/";
import {
    AggregatorFailureResponse,
    AggregatorReportResponse,
    AggregatorStatusResponse,
    AvailableAggregatorsResponse,
    CreateAggregatorRequest,
    CreateAggregatorResponse,
    DeleteAggregatorRequest,
    StartAggregatorRequest
} from "../server/messages/aggregation/";
import { ActivityProgress }
    from "../server/messages/aggregation/activity-progress";
import { AcknowledgmentResponse, ReplyVerifyingMessage }
    from "../server/messages/general/";
import {
    ChangeAggregationSchemaVisibility,
    ChangeDataExportSchemaVisibility,
    DeleteAggregationSchemaRequest,
    DeleteDataExportSchemaRequest,
    RefreshReportSchemasRequest,
    SaveAggregrationSchemaRequest,
    SaveDataExportSchemaRequest
} from "../server/messages/schemas/";

import {
    AvailableDataExportsResponse,
    CreateDataExportRequest,
    CreateDataExportResponse,
    DataExportReportResponse,
    DataExportStatusRequest,
    DataExportStatusResponse,
    RefreshReportSchemasResponse,
    StartDataExportRequest,
    StartDataExportResponse
} from "../server/messages/export/";

import { AggregationFilterNode }
    from "../server/messages/aggregation/start-aggregator-request";
import { MessagingService } from "../server/messaging.service";
import { disconnectEmpty, disconnectValue } from "../support/types/decorators";
import { arrayRemove, identity, oneline } from "../support/utilities";

import { Aggregation } from "./aggregation";
import { Aggregator, AggregatorResponseType } from "./aggregator";
import { AggregatorDescriptor } from "./aggregator-descriptor";
import { DataExport } from "./data-export";
import { Exporter, ExportFormatDescriptor } from "./exporter";
import { AggregationSchema, DataExportSchema, ToolSchema } from "./schemas";
import { ActivityState, ToolboxActivity } from "./toolbox-activity";

export type Tool = Aggregator | Exporter;

export enum ToolUIPanel {
    Analysis,
    Export,
    Schemas
}

/**
 * The {@link AggregrationService}
 */
@Injectable()
export class AggregationService implements MessageReceiver
{
    @disconnectEmpty()
    public aggregators : Aggregator[] = [];

    @disconnectEmpty()
    public aggregations : Aggregation[] = [];

    @disconnectEmpty()
    public aggregationSchemas : AggregationSchema[] = [];

    @disconnectEmpty()
    public exporters : Exporter[] = [];

    @disconnectEmpty()
    public exports : DataExport[] = [];

    @disconnectEmpty()
    public exportSchemas : DataExportSchema[] = [];

    @disconnectValue(0)
    public schemaCount : number = 0;

    @disconnectValue(ToolUIPanel.Analysis)
    public selectedPanel : ToolUIPanel = ToolUIPanel.Analysis;

    constructor (
        public messaging : MessagingService,
        public algorithms : AlgorithmService,
        protected sanitizer : DomSanitizer,
        protected alerts : AlertService,
        public users : UserService,
        protected settings : SettingService,
        protected router : Router
    )
    {
        this.messaging.ready.subscribe(
            (on : boolean) => on ? this.connected() : this.disconnected()
        );
    }

    public connected () : void
    {
        this.messaging.receive<AvailableAggregatorsResponse>(
            MessageTag.RSP_AVAILABLE_AGGREGATORS
        ).flatMap(
            (aar : AvailableAggregatorsResponse) => aar.aggregators
        ).subscribe(
            (desc : AggregatorDescriptor) => {
                this.aggregators.push(Aggregator.fromDescriptor(desc));
            }
        );

        this.messaging.receive<AvailableDataExportsResponse>(
            MessageTag.RSP_AVAILABLE_DATA_EXPORTS
        ).flatMap(
            (ader : AvailableDataExportsResponse) => ader.formats
        ).subscribe(
            (desc : ExportFormatDescriptor) => {
                this.exporters.push(Exporter.fromDescriptor(desc));
            }
        );

        this.messaging.receiveAnyOf([
            MessageTag.RSP_DATA_EXPORT_REPORT,
            MessageTag.RSP_AGGREGATOR_REPORT
        ]).subscribe(
            (report : AggregatorReportResponse | DataExportReportResponse) => {
                const existing : ToolboxActivity =
                    (report instanceof AggregatorReportResponse)
                        ? this.aggregations.find(
                            (x : Aggregation) => x.id === report.aggregationId)
                        : this.exports.find(
                            (x : DataExport) => x.id === report.exportId);
                if (existing)
                {
                    existing.readMessageReport(report);
                    if (!this.router.isActive(AGGREGATION_MODULE_URL, false))
                    {
                        this.alerts.add({
                            type: "success",
                            message: oneline`
                                Tool <em>"${existing.title}"</em>
                                has finished running`,
                            timeout: this.alerts.getDefaultTimeout(),
                            actionLabel: "View",
                            action: () => {
                                this.router.navigateByUrl(
                                    AGGREGATION_MODULE_URL);
                                this.selectedPanel =
                                    existing instanceof Aggregation
                                    ? ToolUIPanel.Analysis
                                    : ToolUIPanel.Export;
                            }
                        });
                    }
                }
                else
                {
                    const id : number =
                        (report as any).exportId
                        || (report as any).aggregationId;
                    throw new Error(`Received report for unknown task ${id}`);
                    // this.aggregations.unshift(DataExport.fromReport(derr));
                }
            });

        this.messaging.receiveAnyOf([
            MessageTag.RSP_STATUS_AGGREGATOR,
            MessageTag.RSP_STATUS_DATA_EXPORT
        ]).subscribe(
            (status : AggregatorStatusResponse | DataExportStatusResponse) => {
                const existing : ToolboxActivity =
                    (status instanceof AggregatorStatusResponse)
                        ? this.aggregations.find(
                            (x : Aggregation) => x.id === status.aggregationId)
                        : this.exports.find(
                            (x : DataExport) => x.id === status.exportId);
                if (existing)
                {
                    existing.readProgress(status);
                }
                else
                {
                    const id : number =
                        (status as any).exportId
                        || (status as any).aggregationId;
                    throw new Error(`Received status for unknown task ${id}`);
                }
            });

        this.messaging.receive(MessageTag.RSP_REFRESH_REPORT_SCHEMAS).subscribe(
            (schemaResponse : RefreshReportSchemasResponse) =>
                this.processSchemas(schemaResponse)
        );
    }

    public disconnected () : void
    {
        this.aggregators = [];
        this.aggregations = [];
    }

    public startConfiguration (
        tool : Tool,
        algorithmName : string,
        group : Group
    ) : void
    {
        const algorithm : Algorithm =
            this.algorithms.findAlgorithm(algorithmName);
        if (tool instanceof Aggregator)
        {
            this.configureAggregator(tool, algorithm, group).subscribe();
        }
        else if (tool instanceof Exporter)
        {
            this.configureExporter(tool, algorithm, group).subscribe();
        }
        else
        {
            throw new Error(`Unknown tool type: ${tool}`);
        }
    }

    protected processSchemas (schemaResponse : RefreshReportSchemasResponse)
        : void
    {
        this.exportSchemas = schemaResponse.dataExportSchemas;
        this.aggregationSchemas = schemaResponse.aggregationSchemas;
        this.schemaCount =
            this.exportSchemas.length + this.aggregationSchemas.length;

        // There's no guarantee that we process schemas after the user or algo
        // info returns, so wait until the services say they're ready to find
        // the actual group and algorithm values for the ids/names here.
        this.users.ready.first(identity).subscribe(() => {
            this.exportSchemas.forEach(
                (schema : DataExportSchema) =>
                    schema.group = this.users.self.groups.find(
                        (g : Group) => g.id === schema.groupId));
            this.aggregationSchemas.forEach(
                (schema : AggregationSchema) =>
                    schema.group = this.users.self.groups.find(
                        (g : Group) => g.id === schema.groupId));
        });

        this.algorithms.ready.first(identity).subscribe(() => {
            this.exportSchemas.forEach(
                (schema : DataExportSchema) =>
                    schema.algorithmObject =
                        this.algorithms.findAlgorithm(schema.algorithm));
            this.aggregationSchemas.forEach(
                (schema : AggregationSchema) =>
                    schema.algorithmObject =
                        this.algorithms.findAlgorithm(schema.algorithm));
        });
    }

    public startFromSchema (
        schema : ToolSchema,
        isExport : boolean
    ) : void
    {
        let configured : Observable<ToolboxActivity>;
        const algorithm : Algorithm =
            this.algorithms.findAlgorithm(schema.algorithm);
        const group : Group = this.users.self.groups.find(
            (g : Group) => g.id === schema.groupId);
        if (isExport)
        {
            const exporter : Exporter = this.exporters.find(
                (e : Exporter) =>
                    e.name === (schema as DataExportSchema).format.formatName);
            configured = this.configureExporter(exporter, algorithm, group);
        }
        else
        {
            const aggregator : Aggregator = this.aggregators.find(
                (a : Aggregator) =>
                    a.name === (schema as AggregationSchema).aggregatorName);
            configured = this.configureAggregator(aggregator, algorithm, group);
        }
        configured.subscribe((ta : ToolboxActivity) => {
            console.log("start schema", ta, schema);
            ta.readSchema(schema);
            // if (runImmediate)
            // {
            //     this.run(ta);
            // }
        });
    }

    public duplicateExport (dexport : DataExport) : void
    {
        //
        const algorithm : Algorithm =
            this.algorithms.findAlgorithm(dexport.algorithm);
        const group : Group = this.users.self.groups.find(
            (g : Group) => g.id === dexport.groupId);
        this.configureExporter(dexport.parent, algorithm, group).subscribe(
            (newExport : DataExport) => newExport.copyFrom(dexport));
    }

    protected configureAggregator (
        aggregator : Aggregator,
        algorithm : Algorithm,
        group : Group
    ) : Observable<Aggregation>
    {
        return this.messaging.converse(
            new CreateAggregatorRequest(
                aggregator.name,
                algorithm.name,
                algorithm.contextType,
                group.id)
        ).map((car : CreateAggregatorResponse) => {
            const agg : Aggregation = Aggregation.fromOptions(car, aggregator);
            agg.algorithm = algorithm.displayName;
            agg.group = group.name;
            this.aggregations.unshift(agg);
            this.selectedPanel = ToolUIPanel.Analysis;
            return agg;
        });
    }

    protected configureExporter (
        exporter : Exporter,
        algorithm : Algorithm,
        group : Group
    ) : Observable<DataExport>
    {
        return this.messaging.converse(
                    new CreateDataExportRequest(
                    exporter.formatName,
                    algorithm.name,
                    algorithm.contextType,
                    group.id
                )
            ).map((cder : CreateDataExportResponse) => {
                const exp : DataExport = DataExport.fromOptions(cder, exporter);
                exp.algorithm = algorithm.displayName;
                exp.group = group.name;
                exp.groupId = group.id;
                this.exports.unshift(exp);
                this.selectedPanel = ToolUIPanel.Export;
                return exp;
            });
    }

    public run (task : ToolboxActivity) : void
    {
        if (task instanceof Aggregation)
        {
            this.runAggregation(task);
        }
        else if (task instanceof DataExport)
        {
            this.runExport(task);
        }
        else
        {
            throw new Error(`Unknown toolbox activity: ${task}`);
        }
    }

    protected runAggregation (aggregation : Aggregation) : void
    {
        let filter : AggregationFilterNode;
        try
        {
            filter = aggregation.filter
                ? aggregation.filter.marshal()
                : null;
        }
        catch (err)
        {
            alert("The filter you provided is incomplete or misconfigured.");
            return;
        }

        this.messaging.successful(new StartAggregatorRequest(
            aggregation.id,
            filter,
            aggregation.targetSelector.value,
            // set the rerun flag if the aggregation has already been completed:
            aggregation.isComplete
        )).subscribe(
            () => aggregation.markScheduled(),
            (err : any) => alert(err)
        );
    }

    protected runExport (dexport : DataExport) : void
    {
        let filter : AggregationFilterNode;
        try
        {
            filter = dexport.filter
                ? dexport.filter.marshal()
                : null;
        }
        catch (err)
        {
            alert("The filter you provided is incomplete or misconfigured.");
            return;
        }
        console.groupCollapsed(`Verbose export debugging ${dexport.id}`);

        this.messaging.converse(new StartDataExportRequest(
            dexport.id,
            filter,
            dexport.columns,
            dexport.headersControl.value,
            dexport.delimiterControl.value,
            dexport.excelDatesControl.value,
            dexport.dropCollFilterEmptiesControl.value
        )).subscribe(
            (ser : StartDataExportResponse) => {
                dexport.markScheduled();
                dexport.downloadCredentials =
                    {token: ser.urlId, secret: ser.token};
                this.downloadExport(dexport);
            },
            (err : any) => alert(err)
        );
    }

    protected downloadExport (dexport : DataExport) : void
    {
        if (!dexport.downloadCredentials)
        {
            throw new Error("Export does not yet have download credentials");
        }
        Observable.zip(
            this.messaging.downloadFile(
                dexport.downloadCredentials.token,
                dexport.downloadCredentials.secret,
                dexport.parent.mimeType
            ),
            this.messaging.receive<DataExportReportResponse>(
                MessageTag.RSP_DATA_EXPORT_REPORT).filter(
                    (message : DataExportReportResponse) =>
                        message.exportId === dexport.id)
        )
        .flatMap(([file, derr] : [SocketFile, DataExportReportResponse]) =>
            file.verify(derr.bytesSent, derr.checksumUnsigned).map(
                (v : boolean) => [file, v])
        ).subscribe(([file, verified] : [SocketFile, boolean]) => {
            console.groupEnd();
            if (verified)
            {
                dexport.downloadFile = file;
                if (this.settings.get("experimental CSV fix") === true)
                {
                    file.addBOM();
                    dexport.expectedFilesize += SocketFile.BOMByteLength;
                }
                this.generateFileURLs(dexport);
            }
            else
            {
                dexport.downloadFile = file;
                this.alerts.error(oneline`
                    The file sent by the server failed validation
                    and could not be downloaded.`);
            }
        });
    }

    public generateFileURLs (task : DataExport) : void
    {
        task.downloadURL = task.downloadFile.generateUrl();
        task.downloadSafeURL = this.sanitizer.bypassSecurityTrustUrl(
            task.downloadURL);
    }

    public cancel (activity : ToolboxActivity) : void
    {
        this.messaging.successful(
            activity.requestFor("cancel")).subscribe(
                () => activity.state = ActivityState.Aborted);
    }

    public pause (activity : ToolboxActivity) : void
    {
        if (activity.state !== ActivityState.Running)
        {
            console.error(activity);
            throw new Error(`Can only pause a running activity!`);
        }
        this.messaging.successful(
            activity.requestFor("pause")).subscribe(
                () => activity.state = ActivityState.Paused);
    }

    public resume (activity : ToolboxActivity) : void
    {
        if (activity.state !== ActivityState.Paused)
        {
            console.error(activity);
            throw new Error(`Can only resume a paused activity!`);
        }
        this.messaging.successful(
            activity.requestFor("resume")).subscribe(
                () => activity.state = ActivityState.Running);
    }

    public release (activity : ToolboxActivity) : void
    {
        if (activity instanceof Aggregation)
        {
            this.messaging.successful(
                new DeleteAggregatorRequest(activity.id)
            ).subscribe(() => {
                activity.state = ActivityState.Released;
                arrayRemove(this.aggregations, activity);
            });
        }
        else
        {
            console.warn(
                `[WARNING] No impl for telling server to release export agent`);
            arrayRemove(this.exports, activity);
            URL.revokeObjectURL((activity as DataExport).downloadURL);
        }
    }

    public requestProgress (activity : ToolboxActivity) : void
    {
        this.messaging.converse(
             activity.requestFor("status")
        ).subscribe((asr : Message & ActivityProgress) => {
            activity.readProgress(asr);
        });
    }

    public saveSchema (
        activity : ToolboxActivity,
        name : string,
        groupVisible : boolean = false
    ) : Observable<true>
    {
        if (activity instanceof DataExport)
        {
            return this.messaging.converse(
                new SaveDataExportSchemaRequest(
                    activity.id,
                    name,
                    groupVisible,
                    activity.filenameControl.value)
            ).flatMap(
                () => this.messaging.successful(
                    new RefreshReportSchemasRequest())
            );
        }
        else
        {
            return this.messaging.converse(
                new SaveAggregrationSchemaRequest(
                    activity.id,
                    name,
                    groupVisible)
            ).flatMap(
                () => this.messaging.successful(
                    new RefreshReportSchemasRequest())
            );
        }
    }

    public deleteSchema (schema : ToolSchema, isExport : boolean) : void
    {
        const msg : ReplyVerifyingMessage = isExport
            ? new DeleteDataExportSchemaRequest(schema.schemaId)
            : new DeleteAggregationSchemaRequest(schema.schemaId);
        this.messaging.converse(msg).subscribe(
            (result : AcknowledgmentResponse) => {
                if (result.ok)
                {
                    arrayRemove(
                        (isExport
                            ? this.exportSchemas
                            : this.aggregationSchemas) as ToolSchema[],
                        schema);
                }
                else
                {
                    const addl : string = result.reason || "unknown reason";
                    this.alerts.error(`Could not remove template: ${addl}`);
                }
            }
        );
    }

    public toggleSchemaVisibility (schema : ToolSchema, isExport : boolean)
        : void
    {
        const msg : ReplyVerifyingMessage = isExport
            ? new ChangeDataExportSchemaVisibility(
                schema.schemaId, !schema.isGroupPublic)
            : new ChangeAggregationSchemaVisibility(
                schema.schemaId, !schema.isGroupPublic);
        this.messaging.converse(msg).subscribe(
            () => schema.isGroupPublic = !schema.isGroupPublic);
    }

    public renameSchema (schema : ToolSchema, newName : string) : void
    {
        throw new Error("Not yet implemented");
        // const msg : EditSchemaRequest =
        //    new EditSchemaRequest(schema.schemaId, newName);
        // this.messaging.converse(msg).subscribe(
        //     (rsp : AcknowledgmentResponse) => {
        //         if (rsp.ok)
        //         {
        //             schema.schemaName = newName;
        //         }
        //         else
        //         {
        //             this.alerts.warn(
        //                 `Could not update schema name: ${rsp.reason}`);
        //         }
        //     });
    }

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/aggregation/aggregation.service.ts